import { useEffect } from 'react';
import Head from 'next/head';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getWindow, trackEvent, trackNavigatedToPage } from '@surfline/web-common';

import Gallery from 'components/Gallery';
import IdealConditions from 'components/IdealConditions';
import SpotDescriptor from 'components/SpotDescriptor';
import config from 'config';
import stripHTMLFromText from 'utils/stripHTMLFromText';
import { DEFAULT_IMAGE } from 'utils/transformers';

import Styles from './Spot.module.scss';

const upperCaseFirstLetter = (string: string) => {
  const allLower = string.toLowerCase();
  return `${allLower.charAt(0).toUpperCase()}${allLower.slice(1)}`;
};

const allLevels = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED'];

const AbilityLevelsSpotDescriptor = ({ data }: { data: AbilityLevels }) => {
  const value = allLevels.every((val) => data?.levels.includes(val))
    ? 'All Abilities'
    : data?.levels.map((level) => upperCaseFirstLetter(level.toLowerCase())).join(' - ');

  const options = allLevels.map((level) => ({
    name: upperCaseFirstLetter(level.slice(0, 3)),
    value: data?.levels.includes(level),
  }));

  return (
    <SpotDescriptor
      headingComponent="h3"
      kind="Ability Level"
      value={value}
      options={options}
      description={data?.description}
      scale={undefined}
    />
  );
};

interface AbilityLevels {
  description: string;
  levels: string[];
  summary: string;
}

export interface SpotProps {
  page: {
    links?: {
      nextLink?: string;
      nextLinkAs?: string;
      previousLink?: string;
      previousLinkAs?: string;
    };
    spot: {
      id?: string;
      title?: string;
      slug?: string;
      zone?: {
        id: string;
        title: string;
        slug: string;
      };
      region?: {
        id: string;
        title: string;
      };
      abilityLevels: AbilityLevels;
      description: string;
      levels?: string[];
      summary?: string;
      best?: {
        season: {
          description: string;
          value: string[];
        };
        tide: {
          description: string;
          value: string[];
        };
        size: {
          description: string;
          value: string[];
        };
        windDirection: {
          description: string;
          value: string[];
        };
        swellDirection: {
          description: string;
          value: string[];
        };
      };
      bottom?: {
        description: string;
        value: string[];
      };
      crowdFactor?: {
        description: string;
        rating: number;
        summary: string;
      };
      localVibe?: {
        description: string;
        rating: number;
        summary: string;
      };
      shoulderBurn?: {
        description: string;
        rating: number;
        summary: string;
      };
      spotRating?: {
        description: string;
        rating: number;
        summary: string;
      };
      waterQuality?: {
        description: string;
        rating: number;
        summary: string;
      };
      travelId?: string;
      access?: string;
      breakType?: string[];
      hazards?: string;
      relatedArticleId?: string;
      status?: string;
      boardTypes?: string[];
      media?: { title: string; contentType: string; url: string }[];
    };
  };
}

export const Spot: React.FC<SpotProps> = ({ page }) => {
  const { links, spot } = page;
  const { asPath } = useRouter();

  useEffect(() => {
    if (spot) {
      trackNavigatedToPage('Page Viewed', {
        category: 'travel',
        path: asPath,
        referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
        search: getWindow().location.search,
        spotId: spot?.id,
        title: `${spot?.zone?.title} ${spot?.title}`,
        url: `${getWindow()?.location?.href ?? ''}${asPath}`,
      });
    }
  }, [spot]);

  const trackButtonClick = (direction: 'previous' | 'next') => {
    trackEvent('Carousel Clicked', {
      carouselItem: direction,
      carouselName: 'The Waves Modal',
      category: 'travel',
      path: asPath,
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow().location.search,
      title: `${spot?.zone?.title} ${spot?.title}`,
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
    });
  };

  if (!spot) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
          padding: '60px',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const media = spot?.media?.map((item) => ({
    id: item.url,
    alt: item?.title,
    large: config.cloudflareImageResizingUrl(item.url, 'h=1400,w=2500,q=85,f=auto,fit=contain'),
    medium: config.cloudflareImageResizingUrl(item.url, 'h=700,w=1250,q=85,f=auto,fit=contain'),
    small: config.cloudflareImageResizingUrl(item.url, 'h=200,w=200,q=85,f=auto,fit=contain'),
    src: item.url,
  }));

  return (
    <>
      <Head>
        <title>{config.meta.title.travel.spot(spot?.title ?? '')}</title>
        <meta
          property="og:image"
          content={
            media && media.length > 0
              ? config.cloudflareImageResizingUrl(
                  media?.[0]?.src,
                  'w=1600,h=900,q=85,f=auto,fit=contain',
                )
              : DEFAULT_IMAGE
          }
        />
      </Head>
      <Box id="main" className={Styles.spot} key={spot?.id}>
        <header className={Styles.header} data-testid="travel-spot-header">
          <div className={Styles.headerTextWrap}>
            <Typography className={Styles.headerTitle} variant="title1">
              {spot?.title}
            </Typography>
            {spot?.zone?.title && (
              <Link
                href={`/travel/zones/${(
                  spot?.zone?.slug ?? spot?.zone?.title
                )?.toLocaleLowerCase()}/${spot?.zone?.id}`}
                className={Styles.zoneLink}
              >
                <Typography className={Styles.headerSubtitle} variant="body" color="text.secondary">
                  {spot?.zone?.title} Surf Spots
                </Typography>
              </Link>
            )}
            {links && (
              <nav className={Styles.nav}>
                <Link href={links.previousLink || ''} as={links.previousLinkAs} shallow>
                  <Button
                    variant="secondary"
                    size="small"
                    className={Styles.navButton}
                    onClick={() => trackButtonClick('previous')}
                  >
                    Previous spot
                  </Button>
                </Link>
                <Link href={links.nextLink || ''} as={links.nextLinkAs} shallow>
                  <Button
                    variant="secondary"
                    size="small"
                    sx={{ marginLeft: 1 }}
                    className={Styles.navButton}
                    onClick={() => trackButtonClick('next')}
                  >
                    Next Spot
                  </Button>
                </Link>
              </nav>
            )}
          </div>
        </header>
        <div>
          <Gallery items={media} />
          <div className={Styles.details}>
            <Typography variant="title2" mb={3}>
              Surf Guide
            </Typography>
            <Typography variant="body" className={Styles.body}>
              {stripHTMLFromText(spot?.description)}
            </Typography>
            <Grid
              container
              justifyContent="center"
              pb={{ mobile: 2, tabletLg: 8 }}
              pl={{ mobile: 0, tabletLg: 8 }}
              pr={{ mobile: 0, tabletLg: 8 }}
              pt={{ mobile: 2, tabletLg: 8 }}
              spacing={{ mobile: 6, tabletLg: 8 }}
            >
              {spot?.abilityLevels && (
                <Grid item mobile={12} tablet={6} tabletLg={4}>
                  <AbilityLevelsSpotDescriptor data={spot.abilityLevels} />
                </Grid>
              )}
              {spot?.localVibe?.rating && (
                <Grid
                  item
                  mobile={12}
                  tablet={6}
                  tabletLg={4}
                  style={{
                    // @ts-ignore
                    '--bar-bg': `linear-gradient(to right, #8CBCFF ${
                      spot.localVibe.rating * 10
                    }%, #E4E7ED 0)`,
                  }}
                >
                  <SpotDescriptor
                    headingComponent="h3"
                    kind="Local Vibe"
                    value={spot.localVibe?.summary}
                    options={['Welcoming', 'Intimidating']}
                    description={spot.localVibe?.description}
                    scale={spot.localVibe.rating}
                  />
                </Grid>
              )}
              {spot?.crowdFactor?.rating && (
                <Grid
                  item
                  mobile={12}
                  tablet={6}
                  tabletLg={4}
                  style={{
                    // @ts-ignore
                    '--bar-bg': `linear-gradient(to right, #8CBCFF ${
                      spot.crowdFactor.rating * 10
                    }%, #E4E7ED 0)`,
                  }}
                >
                  <SpotDescriptor
                    headingComponent="h3"
                    kind="Crowd Factor"
                    value={spot.crowdFactor?.summary}
                    options={['Mellow', 'Heavy']}
                    description={spot.crowdFactor?.description}
                    scale={spot.crowdFactor.rating}
                  />
                </Grid>
              )}
              {spot?.spotRating?.rating && (
                <Grid
                  item
                  mobile={12}
                  tablet={6}
                  tabletLg={4}
                  style={{
                    // @ts-ignore
                    '--bar-bg': `linear-gradient(to right, #8CBCFF ${
                      spot.spotRating.rating * 10
                    }%, #E4E7ED 0)`,
                  }}
                >
                  <SpotDescriptor
                    headingComponent="h3"
                    kind="Spot Rating"
                    value={spot.spotRating?.summary}
                    options={['Poor', 'Perfect']}
                    description={spot.spotRating?.description}
                    scale={spot.spotRating.rating}
                  />
                </Grid>
              )}
              {spot?.shoulderBurn?.rating && (
                <Grid
                  item
                  mobile={12}
                  tablet={6}
                  tabletLg={4}
                  style={{
                    // @ts-ignore
                    '--bar-bg': `linear-gradient(to right, #8CBCFF ${
                      spot.shoulderBurn.rating * 10
                    }%, #E4E7ED 0)`,
                  }}
                >
                  <SpotDescriptor
                    headingComponent="h3"
                    kind="Shoulder Burn"
                    value={spot.shoulderBurn?.summary}
                    options={['Light', 'Exhausting']}
                    description={spot.shoulderBurn?.description}
                    scale={spot.shoulderBurn.rating}
                  />
                </Grid>
              )}
              {spot?.waterQuality?.rating && (
                <Grid
                  item
                  mobile={12}
                  tablet={6}
                  tabletLg={4}
                  style={{
                    // @ts-ignore
                    '--bar-bg': `linear-gradient(to right, #8CBCFF ${
                      spot.waterQuality.rating * 10
                    }%, #E4E7ED 0)`,
                  }}
                >
                  <SpotDescriptor
                    headingComponent="h3"
                    kind="Water Quality"
                    value={spot.waterQuality?.summary}
                    options={['Clean', 'Dirty']}
                    description={spot.waterQuality?.description}
                    scale={spot.waterQuality.rating}
                  />
                </Grid>
              )}
            </Grid>
            <IdealConditions
              conditions={{
                size: {
                  description: spot?.best?.size?.description,
                },
                swellDirection: {
                  description: spot?.best?.swellDirection?.description,
                },
                tide: {
                  description: spot?.best?.tide?.description,
                },
                windDirection: {
                  description: spot?.best?.windDirection?.description,
                },
              }}
            />
          </div>
        </div>
      </Box>
    </>
  );
};

export default Spot;
